import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, FileSignature, ArrowRight, Plus, UserPlus, CheckCircle2, Clock } from "lucide-react";
import api from "../lib/api";

/**
 * Tools → Lead onboarding landing page.
 *
 * Picker surface for starting / resuming the detailed lead-onboarding fact-
 * find form. Shows every lead grouped by onboarding status so a rep can
 * quickly jump back into an in-progress form or kick off a fresh one.
 *
 * Use-cases:
 *   1. Rep met a prospect — tap "Start for new lead" → creates a blank lead
 *      and drops them straight into the onboarding form.
 *   2. Rep is following up — type the lead name, click "Continue" → deep-
 *      links to `/leads/:id/onboarding`.
 */
export default function ToolsOnboarding() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // Iter 411 (Dirk, 2026-02-28) — Assign a rep/manager at onboarding
  // time. When set, the backend auto-creates an "Onboard lead" task
  // for that user.
  const [staff, setStaff] = useState([]);
  const [assignee, setAssignee] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [lRes, uRes] = await Promise.all([
        api.get("/leads"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setLeads(lRes.data || []);
      const users = uRes.data || [];
      setStaff(users.filter((u) => ["owner", "admin", "manager", "rep"].includes(u.role)));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // `?new=prospect` → auto-spawn a blank lead and jump straight into the
  // detailed fact-find form. Used by the "New prospect" button in Leads +
  // sidebar + ToolsIndex.
  useEffect(() => {
    if (searchParams.get("new") === "prospect" && !creating) {
      setSearchParams({}, { replace: true });
      createBlankLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return leads;
    return leads.filter(
      (l) =>
        (l.title || "").toLowerCase().includes(s) ||
        (l.prospect_name || l.customer_name || "").toLowerCase().includes(s) ||
        (l.email || "").toLowerCase().includes(s) ||
        (l.phone || "").includes(q),
    );
  }, [leads, q]);

  // 2026-05-13 — Sign-off removed. Onboarding is now in-progress or not-started.
  const inProgress = filtered.filter((l) => l.onboarding || l.onboarding_data || l.onboarding_signed_by);
  const notStarted = filtered.filter((l) => !l.onboarding && !l.onboarding_data && !l.onboarding_signed_by);

  const createBlankLead = async () => {
    setCreating(true);
    try {
      const payload = {
        title: "Lead onboarding",
        phase: "future_prospect",
        source: "Other",
      };
      if (assignee) {
        payload.assigned_user_id = assignee;
        const picked = staff.find((u) => u.id === assignee);
        if (picked) payload.assigned_user_name = picked.name;
      }
      const { data } = await api.post("/leads", payload);
      nav(`/leads/${data.id}/onboarding`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="tools-onboarding-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Onboarding</div>
        <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
          <FileSignature className="w-7 h-7 text-cyan-400" />
          Lead Onboarding
        </h1>
        <p className="text-sm text-[#a0aabf] mt-2 max-w-2xl">
          Detailed fact-find form for new leads. Pick an existing lead to continue, or start a new one.
        </p>
      </div>

      <div className="hv-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, customer, email, phone…"
            className="hv-input hv-input-search w-full"
            data-testid="tools-onboarding-search"
          />
        </div>
        <select
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          className="hv-input text-sm"
          data-testid="tools-onboarding-assignee"
          title="Optionally assign this lead to a rep/manager — auto-creates an onboarding task on their /tasks page."
        >
          <option value="">Unassigned</option>
          {staff.map((u) => (
            <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={createBlankLead}
          disabled={creating}
          className="hv-btn hv-btn-primary"
          data-testid="tools-onboarding-new-lead"
        >
          <UserPlus className="w-4 h-4" /> {creating ? "Creating…" : "Start for a new lead"}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#647087] hv-card p-4">Loading leads…</div>
      ) : (
        <>
          <Section
            title="In progress"
            desc="Forms with data captured — open to keep editing."
            leads={inProgress}
            accent="#10B981"
            icon={CheckCircle2}
            cta="Continue"
          />
          <Section
            title="Not started"
            desc="Leads with no onboarding info yet."
            leads={notStarted}
            accent="#A78BFA"
            icon={Plus}
            cta="Start onboarding"
          />
        </>
      )}
    </div>
  );
}

function Section({ title, desc, leads, accent, icon: Icon, cta }) {
  if (leads.length === 0) return null;
  return (
    <div className="hv-card p-5" data-testid={`tools-onboarding-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: accent }} />
          <h3 className="font-display text-lg font-bold">{title}</h3>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ borderColor: `${accent}40`, color: accent }}>
            {leads.length}
          </span>
        </div>
        <span className="text-xs text-[#647087]">{desc}</span>
      </div>
      <div className="divide-y divide-white/5">
        {leads.map((l) => (
          <Link
            key={l.id}
            to={`/leads/${l.id}/onboarding`}
            data-testid={`tools-onboarding-lead-${l.id}`}
            className="flex items-center gap-3 py-2.5 hover:bg-white/[0.02] rounded px-2 transition"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {l.title || "(untitled lead)"}
              </div>
              <div className="text-xs text-[#647087] truncate">
                {l.prospect_name || l.customer_name || "—"}
                {l.email ? ` · ${l.email}` : ""}
                {l.phone ? ` · ${l.phone}` : ""}
              </div>
            </div>
            <div className="shrink-0 text-xs text-[#a0aabf] inline-flex items-center gap-1">
              {cta} <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
